import { z } from 'zod';

import { PRAYER_SLOTS, USER_ROLES, type PrayerSlot, type UserRole } from '@/shared/model/enums';

/**
 * Phase 9 "Insights" dashboard domain model. Every shape here mirrors a row
 * returned by one of the `analytics_*` SQL-aggregating RPCs in
 * `supabase/migrations/20260722000000_phase9_analytics.sql` — all aggregate/
 * count-level data, never per-user rows (PDPL minimization, D-011).
 */

export const ANALYTICS_GRANULARITIES = ['day', 'week', 'month'] as const;
export type AnalyticsGranularity = (typeof ANALYTICS_GRANULARITIES)[number];

// ---------------------------------------------------------------------------
// analytics_summary()
// ---------------------------------------------------------------------------
export interface AnalyticsSummary {
  readonly totalRequests: number;
  readonly openRequests: number;
  readonly filledRequests: number;
  readonly cancelledRequests: number;
  readonly expiredRequests: number;
  /** 0..1 — filled / (filled + cancelled + expired) terminal requests. */
  readonly fillRate: number;
  readonly avgApplicantsPerRequest: number;
  /**
   * Approximation: derived from `coverage_requests.updated_at` for
   * status = 'filled' rows (no dedicated `filled_at` column). Exact at the
   * moment of fill; can overstate if the request is edited again afterward.
   * See the migration header comment.
   */
  readonly avgTimeToFillHours: number;
  readonly newRequests30d: number;
  readonly newSignups30d: number;
  readonly totalUsers: number;
  readonly openTicketBacklog: number;
  readonly newTickets30d: number;
}

const summaryRowSchema = z.object({
  total_requests: z.number(),
  open_requests: z.number(),
  filled_requests: z.number(),
  cancelled_requests: z.number(),
  expired_requests: z.number(),
  fill_rate: z.number(),
  avg_applicants_per_request: z.number(),
  avg_time_to_fill_hours: z.number(),
  new_requests_30d: z.number(),
  new_signups_30d: z.number(),
  total_users: z.number(),
  open_ticket_backlog: z.number(),
  new_tickets_30d: z.number(),
});

export function mapAnalyticsSummary(row: unknown): AnalyticsSummary {
  const r = summaryRowSchema.parse(row);
  return {
    totalRequests: r.total_requests,
    openRequests: r.open_requests,
    filledRequests: r.filled_requests,
    cancelledRequests: r.cancelled_requests,
    expiredRequests: r.expired_requests,
    fillRate: r.fill_rate,
    avgApplicantsPerRequest: r.avg_applicants_per_request,
    avgTimeToFillHours: r.avg_time_to_fill_hours,
    newRequests30d: r.new_requests_30d,
    newSignups30d: r.new_signups_30d,
    totalUsers: r.total_users,
    openTicketBacklog: r.open_ticket_backlog,
    newTickets30d: r.new_tickets_30d,
  };
}

// ---------------------------------------------------------------------------
// analytics_requests_timeseries()
// ---------------------------------------------------------------------------
export interface RequestsTimeseriesPoint {
  /** ISO date (YYYY-MM-DD) — the bucket start. */
  readonly bucket: string;
  readonly total: number;
  readonly openCount: number;
  readonly filledCount: number;
  readonly cancelledCount: number;
  readonly expiredCount: number;
}

const requestsTimeseriesRowSchema = z.object({
  bucket: z.string(),
  total: z.number(),
  open_count: z.number(),
  filled_count: z.number(),
  cancelled_count: z.number(),
  expired_count: z.number(),
});

export function mapRequestsTimeseriesPoint(row: unknown): RequestsTimeseriesPoint {
  const r = requestsTimeseriesRowSchema.parse(row);
  return {
    bucket: r.bucket,
    total: r.total,
    openCount: r.open_count,
    filledCount: r.filled_count,
    cancelledCount: r.cancelled_count,
    expiredCount: r.expired_count,
  };
}

// ---------------------------------------------------------------------------
// analytics_requests_by_city()
// ---------------------------------------------------------------------------
export interface RequestsByCity {
  /** null = masjid has no city set; the UI shows the "unknown" i18n label. */
  readonly city: string | null;
  readonly total: number;
  readonly openCount: number;
  readonly filledCount: number;
  readonly fillRate: number;
}

const requestsByCityRowSchema = z.object({
  city: z.string().nullable(),
  total: z.number(),
  open_count: z.number(),
  filled_count: z.number(),
  fill_rate: z.number(),
});

export function mapRequestsByCity(row: unknown): RequestsByCity {
  const r = requestsByCityRowSchema.parse(row);
  return {
    city: r.city,
    total: r.total,
    openCount: r.open_count,
    filledCount: r.filled_count,
    fillRate: r.fill_rate,
  };
}

// ---------------------------------------------------------------------------
// analytics_requests_by_slot()
// ---------------------------------------------------------------------------
export interface RequestsBySlot {
  readonly slot: PrayerSlot;
  readonly count: number;
}

const requestsBySlotRowSchema = z.object({
  slot: z.enum(PRAYER_SLOTS),
  count: z.number(),
});

export function mapRequestsBySlot(row: unknown): RequestsBySlot {
  const r = requestsBySlotRowSchema.parse(row);
  return { slot: r.slot, count: r.count };
}

// ---------------------------------------------------------------------------
// analytics_signups_timeseries()
// ---------------------------------------------------------------------------
export interface SignupsTimeseriesPoint {
  readonly bucket: string;
  readonly role: UserRole;
  readonly isFreelance: boolean;
  readonly count: number;
}

const signupsTimeseriesRowSchema = z.object({
  bucket: z.string(),
  role: z.enum(USER_ROLES),
  is_freelance: z.boolean(),
  count: z.number(),
});

export function mapSignupsTimeseriesPoint(row: unknown): SignupsTimeseriesPoint {
  const r = signupsTimeseriesRowSchema.parse(row);
  return { bucket: r.bucket, role: r.role, isFreelance: r.is_freelance, count: r.count };
}

// ---------------------------------------------------------------------------
// analytics_support_timeseries()
// ---------------------------------------------------------------------------
export interface SupportTimeseriesPoint {
  readonly bucket: string;
  readonly count: number;
}

const supportTimeseriesRowSchema = z.object({
  bucket: z.string(),
  count: z.number(),
});

export function mapSupportTimeseriesPoint(row: unknown): SupportTimeseriesPoint {
  const r = supportTimeseriesRowSchema.parse(row);
  return { bucket: r.bucket, count: r.count };
}
